import { produce } from "immer";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";

import {
  getTermAggregationBuckets,
  objectiveCompareFn,
} from "@examspark/common";
import { useQueriesGetObjectives } from "@examspark/common-app";
import { HSpace, Tooltip } from "@examspark/common-ui";
import { IconInfoSolid } from "@examspark/common-ui/dist/components/icons";
import { useDebounce } from "@examspark/common-ui/dist/hooks/useDebounce";
import {
  getObjectiveCategory,
  ObjectiveCategoryBadge,
} from "@examspark/customer/tcmpc";

import { FilterData } from "../Browse";
import { useSearch } from "../useSearch";

import FilterCheckboxList from "./FilterCheckboxList";

const ObjectiveInfo = styled.div`
  display: flex;
  align-items: center;

  svg {
    fill: #ccc;
    display: block;
  }
`;

type Props = {
  filters: FilterData;
  onChange: (value: string[] | undefined) => void;
};

const FilterObjectivesStandards = ({ filters, onChange }: Props) => {
  const filter = useMemo(() => {
    const { objectiveIds, ...filter } = filters;
    return filter;
  }, [filters]);

  const debouncedFilter = useDebounce(filter, 500);

  const { data } = useSearch({
    queryKey: ["aggs", "objectives", debouncedFilter],
    searchLabel: "SearchAggsObjectives",
    limit: 0,
    filter: debouncedFilter,
  });

  const objectives = useMemo(() => {
    if (data?.aggregations == null) return undefined;

    const objectives = getTermAggregationBuckets(
      data.aggregations,
      "objectiveId",
    );

    return objectives.map((o) => ({
      ...o,
      id: o.key,
      sk: "standard",
    }));
  }, [data?.aggregations]);

  const onObjectiveToggle = useCallback(
    (id?: string) =>
      onChange(
        produce(filters.objectiveIds, (draft) => {
          if (id == null) return draft;
          if (draft == null) return [id];

          const index = draft.indexOf(id);
          if (index === -1) {
            draft?.push(id);
          } else {
            draft.splice(index, 1);
          }
          return draft;
        }),
      ),
    [onChange, filters],
  );

  const objectivesQueries = useQueriesGetObjectives(objectives ?? []);

  const objectivesOptions = useMemo(() => {
    return objectivesQueries
      .map((q, i) => ({
        data: q.data,
        doc_count: objectives?.[i].doc_count,
        key: objectives?.[i].key,
      }))
      .filter((o) => o.data?.scope === filters?.objectiveScope)
      .sort((a, b) => objectiveCompareFn(a.data, b.data));
  }, [objectivesQueries, objectives, filters?.objectiveScope]);

  if (!objectivesOptions?.length) return null;

  return (
    <FilterCheckboxList>
      {objectivesOptions.map((objective, i) => {
        return (
          <FilterCheckboxList.Item
            key={objective.key}
            count={objective.doc_count ?? 0}
            onClick={() => onObjectiveToggle(objective.key)}
            checked={
              objective.key != null &&
              filters.objectiveIds?.includes(objective.key)
            }
          >
            <HSpace style={{ alignItems: "center" }}>
              <div>{objective.data?.abbreviation}</div>
              <div>
                {objective.data && (
                  <ObjectiveCategoryBadge
                    value={getObjectiveCategory(objective.data)}
                  />
                )}
              </div>
              <div style={{ flex: "1" }}></div>
              <Tooltip
                tip={
                  <div style={{ width: "25rem", zIndex: 9999 }}>
                    {objective.data?.content}
                  </div>
                }
              >
                <ObjectiveInfo>
                  <IconInfoSolid width="16" />
                </ObjectiveInfo>
              </Tooltip>
            </HSpace>
          </FilterCheckboxList.Item>
        );
      })}
    </FilterCheckboxList>
  );
};

export default FilterObjectivesStandards;
